import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { useTelegram, useBackButton, useMainButton } from '@/shared/lib/telegram'
import { useCart } from '@/features/cart/api/use-cart'
import { CartItem, CartSummary, EmptyCart } from '@/features/cart/ui'
import { Skeleton } from '@/shared/ui/skeleton'
import { formatPrice } from '@/shared/lib/format'
import { ShoppingBag, Sparkles } from 'lucide-react'
import { useDeliveryStore, selectDeliveryCost } from '@/features/checkout/model'
import { useDeliveryRecalculation } from '@/features/checkout/lib/use-delivery-recalculation'

// Цвета MainButton для разных тем
const MAIN_BUTTON_COLORS = {
  light: {
    color: '#C73B0D',      // Фирменный красно-глиняный
    textColor: '#FFFFFF',  // Белый текст
  },
  dark: {
    color: '#E84D1C',      // Чуть светлее для тёмной темы
    textColor: '#FFFFFF',  // Белый текст
  },
}

export const Route = createFileRoute('/cart')({
  component: CartPage,
})

/**
 * Страница корзины
 */
function CartPage() {
  const { initData, hapticFeedback, webApp } = useTelegram()
  const navigate = useNavigate()

  // Данные корзины
  const { data, isLoading, error } = useCart()
  const cart = data?.data
  const hasItems = cart && cart.items.length > 0

  // Delivery cost from Zustand — single source of truth
  const deliveryCost = useDeliveryStore(selectDeliveryCost)

  // Auto-recalculate delivery cost when cart changes (shared hook)
  useDeliveryRecalculation()

  // Определяем тему для цветов кнопки
  const theme = webApp?.colorScheme || 'light'
  const buttonColors = MAIN_BUTTON_COLORS[theme]

  // Total including delivery
  const totalWithDelivery = (cart?.totalAmount ?? 0) + deliveryCost

  // Нативная кнопка "Назад" в заголовке Telegram
  useBackButton(true)

  // Нативная MainButton "Оформить" с адаптацией под тему
  useMainButton(hasItems ? {
    text: `Оформить · ${formatPrice(totalWithDelivery)}`,
    onClick: () => navigate({ to: '/checkout' }),
    color: buttonColors.color,
    textColor: buttonColors.textColor,
  } : null)
  
  // Если нет initData
  if (!initData) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg pt-safe">
          <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-dark-elevated flex items-center justify-center mx-auto">
                <ShoppingBag className="h-10 w-10 text-gray-300 dark:text-gray-600" />
              </div>
              <h2 className="text-xl font-bold text-ink dark:text-white">Откройте через Telegram</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs mx-auto">
                Для доступа к корзине откройте приложение через бота @drakonisoroka_bot
              </p>
            </div>
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Загрузка
  if (isLoading) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg pt-safe">
          <div className="container mx-auto px-4 py-6 space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-2xl" />
              ))}
            </div>
            <Skeleton className="h-52 w-full rounded-2xl" />
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Ошибка
  if (error) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg pt-safe">
          <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto">
                <ShoppingBag className="h-8 w-8 text-error" />
              </div>
              <p className="text-error text-lg font-medium">Ошибка загрузки</p>
              <button
                onClick={() => {
                  hapticFeedback?.impactOccurred('light')
                  window.location.reload()
                }}
                className="px-6 py-3 bg-primary text-white rounded-xl font-medium active:scale-[0.98] transition-transform"
              >
                Попробовать снова
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Пустая корзина
  if (!cart || cart.items.length === 0) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg pt-safe">
          <EmptyCart
            onNavigateToCatalog={() => {
              hapticFeedback?.impactOccurred('light')
              navigate({ to: '/catalog/$section', params: { section: 'tea' } })
            }}
          />
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  const getItemsText = (count: number) => {
    if (count === 1) return 'товар'
    if (count >= 2 && count <= 4) return 'товара'
    return 'товаров'
  }

  return (
    <AppShell showBottomNav>
      <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg">
        {/* Хедер */}
        <div className="bg-white dark:bg-dark-card pt-safe border-b border-silk/50 dark:border-dark-border">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-ink dark:text-white">Корзина</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {cart.itemsCount} {getItemsText(cart.itemsCount)}
                </p>
              </div>
              
              <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                <ShoppingBag className="h-5 w-5 text-primary" />
              </div>
            </div>
          </div>
        </div>

        {/* Контент */}
        <div className="container mx-auto px-4 py-4 pb-4">
          <div className="flex flex-col gap-3">

            {/* Подсказка */}
            <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 dark:bg-primary/10 rounded-xl">
              <Sparkles className="h-4 w-4 text-primary" />
              <p className="text-xs text-primary dark:text-primary-light">
                Нажмите на вес, чтобы изменить объём
              </p>
            </div>

            {/* Список товаров */}
            {cart.items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onHapticFeedback={hapticFeedback?.impactOccurred}
              />
            ))}

            {/* Итого — под товарами */}
            <CartSummary
              total={cart.totalAmount}
              itemCount={cart.itemsCount}
            />

          </div>
        </div>
        <div className="bottom-nav-spacer" />
      </div>
      <BottomNav />
    </AppShell>
  )
}
